import type { EnvironmentId, PullRequestDetail, PullRequestRef } from "@cmmd-forge/contracts";
import { RefreshCwIcon } from "lucide-react";
import { lazy, Suspense, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { canEditPullRequest } from "./pullRequestEditing.logic";
import { PullRequestSummary } from "./summary/PullRequestSummary";
import { canWritePullRequestDetail } from "./summary/pullRequestSummary.logic";
import { PullRequestTimeline } from "./timeline/PullRequestTimeline";
import { PullRequestDetailHeader } from "./PullRequestDetailHeader";
import { PullRequestDetailTabs, type PullRequestDetailTabName } from "./PullRequestDetailTabs";
import { usePullRequestDetailPanelModel } from "./usePullRequestDetailPanelModel";
import type { PullRequestAgentSelection } from "~/pullRequest/pullRequestSelectionContext";
import {
  PullRequestHandoffProvider,
  type PullRequestHandoffContextKind,
} from "~/pullRequest/handoff/PullRequestHandoffProvider";

export interface PullRequestDetailPanelProps {
  readonly environmentId: EnvironmentId;
  readonly reference: PullRequestRef;
  /** Where this panel is mounted. Beside a thread there is no checkout to choose. */
  readonly context: PullRequestHandoffContextKind;
}

const LazyPullRequestCodePanel = lazy(() => import("./code/PullRequestCodePanel"));
const preloadPullRequestCodePanel = () => void import("./code/PullRequestCodePanel");

export function PullRequestDetailPanel(props: PullRequestDetailPanelProps) {
  const model = usePullRequestDetailPanelModel(props);
  const state = pullRequestDetailPanelState(model);
  if (state === "loading") return <PullRequestDetailLoading />;
  if (state === "error")
    return (
      <PullRequestDetailError
        error={model.detailQuery.error!}
        onRetry={model.detailQuery.refetch}
      />
    );
  if (state === "empty") return <PullRequestDetailEmpty />;
  return <LoadedPullRequestDetailPanel {...props} detail={model.detail!} model={model} />;
}

type PanelModel = ReturnType<typeof usePullRequestDetailPanelModel>;

function pullRequestDetailPanelState(model: PanelModel) {
  if (model.detail !== undefined) return "loaded" as const;
  if (model.detailQuery.isPending) return "loading" as const;
  return model.detailQuery.error === null ? ("empty" as const) : ("error" as const);
}

function LoadedPullRequestDetailPanel(
  props: PullRequestDetailPanelProps & {
    readonly detail: PullRequestDetail;
    readonly model: PanelModel;
  },
) {
  const [agentSelection, setAgentSelection] = useState<PullRequestAgentSelection | null>(null);
  const [condensed, setCondensed] = useState(false);
  const foldRef = useRef<HTMLElement | null>(null);
  const condensedRowRef = useRef<HTMLElement | null>(null);
  const editable = canWritePullRequestDetail(
    props.model.isDetailFresh,
    canEditPullRequest(props.detail.capabilities.edit),
  );
  return (
    <PullRequestHandoffProvider
      detail={props.detail}
      reference={props.reference}
      activity={props.model.activityQuery.data}
      environmentId={props.environmentId}
      selection={agentSelection}
      context={props.context}
    >
      <div className="flex h-full min-h-0 flex-col bg-background">
        <PullRequestDetailHeader
          detail={props.detail}
          environmentId={props.environmentId}
          model={props.model}
          editable={editable}
          agentSelection={agentSelection}
          condensed={condensed}
          foldRef={foldRef}
          condensedRowRef={condensedRowRef}
        />
        <PullRequestDetailBody
          {...props}
          editable={editable}
          condensed={condensed}
          onAgentSelection={setAgentSelection}
          onChromeCondensedChange={setCondensed}
          foldRef={foldRef}
          condensedRowRef={condensedRowRef}
        />
      </div>
    </PullRequestHandoffProvider>
  );
}

function PullRequestDetailBody(
  props: PullRequestDetailPanelProps & {
    readonly detail: PullRequestDetail;
    readonly editable: boolean;
    readonly condensed: boolean;
    readonly model: PanelModel;
    readonly onAgentSelection: (selection: PullRequestAgentSelection) => void;
    readonly onChromeCondensedChange: (condensed: boolean) => void;
    readonly foldRef: RefObject<HTMLElement | null>;
    readonly condensedRowRef: RefObject<HTMLElement | null>;
  },
) {
  const tabs = usePullRequestDetailTabs(props.detail.capabilities.diff);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PullRequestDetailTabs
        activeTab={tabs.activeTab}
        canViewCode={tabs.canViewCode}
        detail={props.detail}
        activity={props.model.activityQuery.data}
        timelineOrder={tabs.timelineOrder}
        onSelect={(tab) => {
          props.onChromeCondensedChange(tabs.chromeStateByTab.current[tab] ?? false);
          tabs.selectTab(tab);
        }}
        onToggleTimelineOrder={tabs.toggleTimelineOrder}
        onPreloadCode={preloadPullRequestCodePanel}
      />
      <PullRequestDetailTabContent {...props} {...tabs} />
    </div>
  );
}

function usePullRequestDetailTabs(canViewCode: boolean) {
  const [requestedTab, setRequestedTab] = useState<PullRequestDetailTabName>("summary");
  const [visitedTabs, setVisitedTabs] = useState<ReadonlySet<PullRequestDetailTabName>>(
    () => new Set(["summary"]),
  );
  const [timelineOrder, setTimelineOrder] = useState<"newest" | "oldest">("newest");
  const chromeStateByTab = useRef<Partial<Record<PullRequestDetailTabName, boolean>>>({});
  const activeTab = requestedTab === "code" && !canViewCode ? "summary" : requestedTab;
  const selectTab = (tab: PullRequestDetailTabName) => {
    setRequestedTab(tab);
    setVisitedTabs((current) => new Set(current).add(tab));
  };
  return {
    activeTab,
    canViewCode,
    chromeStateByTab,
    selectTab,
    timelineOrder,
    toggleTimelineOrder: () =>
      setTimelineOrder((current) => (current === "newest" ? "oldest" : "newest")),
    visitedTabs,
  };
}

function PullRequestDetailTabContent(
  props: PullRequestDetailPanelProps & {
    readonly detail: PullRequestDetail;
    readonly editable: boolean;
    readonly condensed: boolean;
    readonly model: PanelModel;
    readonly onAgentSelection: (selection: PullRequestAgentSelection) => void;
    readonly onChromeCondensedChange: (condensed: boolean) => void;
    readonly foldRef: RefObject<HTMLElement | null>;
    readonly condensedRowRef: RefObject<HTMLElement | null>;
    readonly activeTab: PullRequestDetailTabName;
    readonly canViewCode: boolean;
    readonly timelineOrder: "newest" | "oldest";
    readonly toggleTimelineOrder: () => void;
    readonly visitedTabs: ReadonlySet<PullRequestDetailTabName>;
    readonly chromeStateByTab: RefObject<Partial<Record<PullRequestDetailTabName, boolean>>>;
  },
) {
  const scrollerRef = useRef<HTMLElement | null>(null);
  const compensationRef = useRef<number | null>(null);
  const measuredFoldHeightRef = useRef(0);
  useLayoutEffect(() => {
    if (compensationRef.current === null) return;
    const delta =
      compensationRef.current +
      (props.condensed ? (props.condensedRowRef.current?.scrollHeight ?? 0) : 0);
    compensationRef.current = null;
    if (scrollerRef.current !== null) {
      scrollerRef.current.scrollTop = Math.max(0, scrollerRef.current.scrollTop + delta);
    }
  }, [props.condensed, props.condensedRowRef]);

  return (
    <div
      className="relative min-h-0 flex-1"
      onScrollCapture={(event) => {
        const scroller = event.target as HTMLElement;
        scrollerRef.current = scroller;
        const previous = props.chromeStateByTab.current[props.activeTab] ?? false;
        const currentFoldHeight = props.foldRef.current?.scrollHeight ?? 0;
        if (currentFoldHeight > 0) measuredFoldHeightRef.current = currentFoldHeight;
        const foldHeight = measuredFoldHeightRef.current;
        const next = resolveCondensedChrome(previous, scroller.scrollTop, foldHeight);
        if (next && !previous) {
          compensationRef.current = -foldHeight;
        }
        if (next !== previous) {
          props.chromeStateByTab.current[props.activeTab] = next;
          props.onChromeCondensedChange(next);
        }
      }}
    >
      <PullRequestSummaryTab {...props} />
      <PullRequestTimelineTab {...props} />
      <PullRequestCodeTabSlot {...props} />
    </div>
  );
}

function resolveCondensedChrome(previous: boolean, scrollTop: number, foldHeight: number): boolean {
  if (foldHeight <= 0) return previous;
  if (previous) return scrollTop >= 4;
  return scrollTop > foldHeight + 32;
}

type PullRequestDetailTabContentProps = Parameters<typeof PullRequestDetailTabContent>[0];

function PullRequestSummaryTab(props: PullRequestDetailTabContentProps) {
  return (
    <section className={props.activeTab === "summary" ? "h-full overflow-y-auto" : "hidden"}>
      <PullRequestSummary
        detail={props.detail}
        activity={props.model.activityQuery.data}
        activityPending={props.model.activityQuery.isPending}
        activityError={props.model.activityQuery.error}
        onRetryActivity={() => void props.model.activityQuery.refetch()}
        editable={props.editable}
        pending={props.model.updateMutation.isPending}
        onSaveDescription={(body) => props.model.updatePullRequest({ body })}
        environmentId={props.environmentId}
        reference={props.reference}
        isDetailFresh={props.model.isDetailFresh}
      />
    </section>
  );
}

function PullRequestTimelineTab(props: PullRequestDetailTabContentProps) {
  if (!props.visitedTabs.has("timeline")) return null;
  return (
    <section className={props.activeTab === "timeline" ? "h-full overflow-y-auto" : "hidden"}>
      <PullRequestTimeline
        detail={props.detail}
        isDetailFresh={props.model.isDetailFresh}
        environmentId={props.environmentId}
        reference={props.reference}
        activity={props.model.activityQuery.data}
        pending={props.model.activityQuery.isPending}
        error={props.model.activityQuery.error}
        order={props.timelineOrder}
        onRetry={props.model.activityQuery.refetch}
      />
    </section>
  );
}

function PullRequestCodeTabSlot(props: PullRequestDetailTabContentProps) {
  if (!props.canViewCode || !props.visitedTabs.has("code")) return null;
  return (
    <section className={props.activeTab === "code" ? "h-full" : "hidden"}>
      <Suspense fallback={<PullRequestDetailLoading />}>
        <LazyPullRequestCodePanel
          environmentId={props.environmentId}
          reference={props.reference}
          detail={props.detail}
          isDetailFresh={props.model.isDetailFresh}
          activity={props.model.activityQuery.data}
          onSelectLines={props.onAgentSelection}
        />
      </Suspense>
    </section>
  );
}

function PullRequestDetailLoading() {
  return (
    <div
      className="flex h-full flex-col gap-4 px-4 py-4"
      aria-label="Loading pull request"
      aria-busy="true"
    >
      <div className="flex items-center justify-between gap-4">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-6 w-20" />
      </div>
      <Skeleton className="h-5 w-4/5" />
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
    </div>
  );
}

function PullRequestDetailEmpty() {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
      This pull request is no longer available from the connected provider.
    </div>
  );
}

function PullRequestDetailError(props: { readonly error: Error; readonly onRetry: () => unknown }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm text-destructive-foreground">
        Pull request details could not be loaded: {props.error.message}
      </p>
      <Button type="button" variant="outline" onClick={() => void props.onRetry()}>
        <RefreshCwIcon className="size-4" /> Retry
      </Button>
    </div>
  );
}
